import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
    ShieldAlert, AlertTriangle, FileWarning, FileX2, Download,
    PlayCircle, ChevronRight, Loader2, CheckCircle2, ExternalLink, Mail,
    History, ScrollText, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";


const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch { return iso; }
};
const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const STATUS_META = {
    pending_review: { label: "Pending review", className: "bg-amber-100 text-amber-800 border-amber-300", icon: AlertTriangle },
    warned: { label: "Warned", className: "bg-orange-100 text-orange-800 border-orange-300", icon: FileWarning },
    revoked: { label: "Revoked", className: "bg-rose-100 text-rose-800 border-rose-300", icon: FileX2 },
    ok: { label: "Cleared", className: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
};

const RISK_META = {
    HIGH: "text-rose-700 font-semibold",
    MEDIUM: "text-amber-700 font-medium",
    LOW: "text-emerald-700",
};

/**
 * Format an axios error into a string that toast.error / React can safely
 * render. FastAPI validation errors return `detail` as an array of objects
 * (e.g. {type, loc, msg, input}), which crashes React when passed to a
 * child slot. This walks the shape and produces a flat human-readable line.
 */
function fmtError(err, fallback = "Action failed.") {
    const detail = err?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((d) => d?.msg || d?.message || "Invalid input")
            .join("; ");
    }
    if (typeof detail === "object") {
        return detail.msg || detail.message || JSON.stringify(detail);
    }
    return String(detail);
}

export default function AdminIntegrity() {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [statusFilter, setStatusFilter] = useState("__all");
    const [running, setRunning] = useState(false);
    const [announceOpen, setAnnounceOpen] = useState(false);

    // Modal state
    const [dossier, setDossier] = useState(null);
    const [dossierLoading, setDossierLoading] = useState(false);
    const [actionType, setActionType] = useState(null); // "warn" | "revoke" | "reenrol" | "clear"

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== "__all") params.status = statusFilter;
            const { data } = await api.get("/admin/integrity/queue", { params });
            setItems(data.items || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(fmtError(e, "Could not load queue."));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const runAudit = async (scope) => {
        setRunning(true);
        try {
            const { data } = await api.post(`/admin/integrity/audit/run?scope=${scope}`);
            toast.success(
                `Audit complete — ${data.total_evaluated} evaluated, ${data.auto_queued} auto-queued (${data.risk_breakdown.HIGH} HIGH)`,
            );
            await load();
        } catch (e) {
            toast.error(fmtError(e, "Audit failed."));
        } finally {
            setRunning(false);
        }
    };

    const downloadCsv = async () => {
        try {
            const resp = await api.get("/admin/integrity/audit/csv", { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `integrity-audit-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch (e) {
            toast.error(fmtError(e, "No audit on file. Run the audit first."));
        }
    };

    const openDossier = async (item) => {
        setDossier({ summary: item });
        setDossierLoading(true);
        try {
            const { data } = await api.get(`/admin/integrity/enrollments/${item.enrollment_id}`);
            setDossier({ summary: item, ...data });
        } catch (e) {
            toast.error("Could not load dossier.");
            setDossier(null);
        } finally {
            setDossierLoading(false);
        }
    };

    const closeAll = () => { setDossier(null); setActionType(null); };

    const filteredItems = useMemo(() => items, [items]);

    return (
        <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto" data-testid="admin-integrity-page">
            {/* Header */}
            <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                        <ShieldAlert className="h-3.5 w-3.5" />
                        Academic Integrity
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl text-foreground mt-1">
                        Integrity review queue
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                        Cases auto-flagged by the retrospective audit, plus any held credentials
                        awaiting registrar decision. All actions are recorded permanently on the
                        student file and notified to the student via formal correspondence.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={downloadCsv}
                        className="gap-1.5"
                        data-testid="audit-download-csv"
                    >
                        <Download className="h-4 w-4" />
                        Audit CSV
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setAnnounceOpen(true)}
                        className="gap-1.5 border-primary/30"
                        data-testid="open-announce-dialog"
                    >
                        <Mail className="h-4 w-4" />
                        Time-gate announcement
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => runAudit("all_completed")}
                        disabled={running}
                        className="gap-1.5"
                        data-testid="audit-run-all"
                    >
                        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                        Audit all completed
                    </Button>
                    <Button
                        onClick={() => runAudit("diploma_completed")}
                        disabled={running}
                        className="bg-primary hover:bg-primary/90 gap-1.5"
                        data-testid="audit-run-diplomas"
                    >
                        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                        Audit diplomas
                    </Button>
                </div>
            </div>

            {/* Tally tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <TallyTile label="Pending review" value={counts.pending_review || 0} variant="amber" testid="tally-pending" />
                <TallyTile label="Warned" value={counts.warned || 0} variant="orange" testid="tally-warned" />
                <TallyTile label="Revoked" value={counts.revoked || 0} variant="rose" testid="tally-revoked" />
                <TallyTile label="Total in queue" value={items.length} variant="primary" testid="tally-total" />
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3 mb-4">
                <Label className="text-xs text-muted-foreground">Filter</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[220px]" data-testid="integrity-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">All cases in queue</SelectItem>
                        <SelectItem value="pending_review">Pending review</SelectItem>
                        <SelectItem value="warned">Warned</SelectItem>
                        <SelectItem value="revoked">Revoked</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Queue table */}
            <Card className="overflow-hidden border-primary/10">
                {loading ? (
                    <div className="p-6 space-y-3">
                        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="p-12 text-center text-sm text-muted-foreground">
                        <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                        <strong>No cases in the queue.</strong>
                        <div className="mt-1">Run an audit above to scan for integrity violations.</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="integrity-table">
                            <thead className="bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-3 font-semibold">Student</th>
                                    <th className="text-left px-4 py-3 font-semibold">Program</th>
                                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                                    <th className="text-left px-4 py-3 font-semibold">Risk</th>
                                    <th className="text-left px-4 py-3 font-semibold">Updated</th>
                                    <th className="text-right px-4 py-3 font-semibold">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((it) => {
                                    const meta = STATUS_META[it.integrity_status] || STATUS_META.pending_review;
                                    const Icon = meta.icon;
                                    return (
                                        <tr
                                            key={it.enrollment_id}
                                            className="border-t border-border hover:bg-muted/20"
                                            data-testid={`integrity-row-${it.enrollment_id}`}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="font-medium">{it.user.name || "—"}</div>
                                                <div className="text-xs text-muted-foreground">{it.user.email}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium truncate max-w-[280px]">{it.program.title}</div>
                                                <div className="text-xs text-muted-foreground capitalize">
                                                    {it.program.credential_type || "certificate"}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge variant="outline" className={`gap-1 text-xs ${meta.className}`}>
                                                    <Icon className="h-3 w-3" />
                                                    {meta.label}
                                                </Badge>
                                            </td>
                                            <td className={`px-4 py-3 ${RISK_META[it.risk_score] || ""}`}>
                                                {it.risk_score || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-muted-foreground">
                                                {fmtDateTime(it.integrity_status_at)}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => openDossier(it)}
                                                    className="gap-1"
                                                    data-testid={`integrity-review-${it.enrollment_id}`}
                                                >
                                                    Review
                                                    <ChevronRight className="h-3 w-3" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <DossierDialog
                dossier={dossier}
                loading={dossierLoading}
                onClose={closeAll}
                onAction={(t) => setActionType(t)}
            />
            <ActionDialog
                actionType={actionType}
                dossier={dossier}
                onClose={() => setActionType(null)}
                onComplete={async () => {
                    setActionType(null);
                    if (dossier?.summary) {
                        // Refresh the dossier from server
                        const { data } = await api.get(
                            `/admin/integrity/enrollments/${dossier.summary.enrollment_id}`,
                        );
                        setDossier((d) => ({ ...d, ...data }));
                    }
                    await load();
                }}
            />
            <AnnouncementDialog
                open={announceOpen}
                onOpenChange={setAnnounceOpen}
            />
        </div>
    );
}

function TallyTile({ label, value, variant, testid }) {
    const variantClasses = {
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        orange: "bg-orange-50 border-orange-200 text-orange-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        primary: "bg-primary/5 border-primary/20 text-primary",
    }[variant] || "bg-card";
    return (
        <Card className={`p-4 ${variantClasses}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold opacity-80">
                {label}
            </div>
            <div className="font-display text-3xl mt-1.5">{value}</div>
        </Card>
    );
}

// ============================================================
// Dossier modal — full evidence + audit trail + action buttons
// ============================================================
function DossierDialog({ dossier, loading, onClose, onAction }) {
    if (!dossier) return null;
    const summary = dossier.summary;
    const ev = dossier.evidence || {};
    const events = dossier.events || [];
    const cert = dossier.certificate;
    const status = dossier.enrollment?.integrity_status || summary.integrity_status;
    const isRevoked = status === "revoked";
    const isWarned = status === "warned";

    return (
        <Dialog open={!!dossier} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="integrity-dossier-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        <ShieldAlert className="h-5 w-5 text-primary" />
                        Integrity case dossier
                    </DialogTitle>
                    <DialogDescription>
                        Evidence packet, certificate status, and registrar-action history for this enrolment.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="space-y-3 py-6">
                        <Skeleton className="h-16 w-full" />
                        <Skeleton className="h-32 w-full" />
                    </div>
                ) : (
                    <div className="space-y-5">
                        {/* Student + program */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Card className="p-3 bg-muted/30">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Student</div>
                                <div className="font-medium">{summary.user.name}</div>
                                <div className="text-xs text-muted-foreground">{summary.user.email}</div>
                                <Link
                                    to={`/admin/students/${summary.user.id}`}
                                    className="text-xs text-primary inline-flex items-center gap-1 mt-1.5 hover:underline"
                                >
                                    Open student dossier
                                    <ExternalLink className="h-3 w-3" />
                                </Link>
                            </Card>
                            <Card className="p-3 bg-muted/30">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Program</div>
                                <div className="font-medium">{summary.program.title}</div>
                                <div className="text-xs text-muted-foreground capitalize">
                                    {summary.program.credential_type || "certificate"} ·
                                    Enrolled {fmtDate(summary.enrolled_at)}
                                    {summary.completed_at ? ` · Completed ${fmtDate(summary.completed_at)}` : ""}
                                </div>
                            </Card>
                        </div>

                        {/* Evidence grid */}
                        <Card className="p-4 border-primary/15">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-xs uppercase tracking-[0.16em] font-semibold text-primary">
                                    Evidence packet
                                </div>
                                <Badge variant="outline" className={`text-[10px] ${RISK_META[dossier.risk_score] || ""}`}>
                                    Risk: {dossier.risk_score || "—"}
                                </Badge>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                <EvidenceCell label="Flagged attempts" value={ev.flagged_attempts_count ?? 0} />
                                <EvidenceCell label="Tab switches" value={ev.tab_switch_count ?? 0} />
                                <EvidenceCell label="Window blurs" value={ev.window_blur_count ?? 0} />
                                <EvidenceCell label="Copy attempts" value={ev.copy_attempts ?? 0} />
                                <EvidenceCell
                                    label="Focused / expected"
                                    value={`${Math.round(ev.focused_minutes || 0)} min / ${Math.round(ev.expected_minutes || 0)} min`}
                                />
                                <EvidenceCell
                                    label="Pace vs intended"
                                    value={ev.pace_ratio_percent != null
                                        ? `${ev.pace_ratio_percent}% (${ev.completion_days || 0}d / ${ev.intended_duration_days || 0}d)`
                                        : "—"}
                                />
                            </div>
                        </Card>

                        {/* Certificate status */}
                        {cert ? (
                            <Card className={`p-4 ${isRevoked ? "border-rose-300 bg-rose-50/50" : "border-emerald-300 bg-emerald-50/40"}`}>
                                <div className="flex items-start gap-3">
                                    {isRevoked ? (
                                        <FileX2 className="h-5 w-5 text-rose-600 mt-0.5" />
                                    ) : (
                                        <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm">
                                            Credential {isRevoked ? "revoked" : "issued"}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                                            {cert.certificate_number || cert.id}
                                        </div>
                                        {isRevoked ? (
                                            <div className="text-xs text-rose-700 mt-1">
                                                Revoked {fmtDate(cert.revoked_at)} — {cert.revoked_reason}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                Issued {fmtDate(cert.issued_at)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ) : null}

                        {/* Audit trail */}
                        <div>
                            <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] font-semibold text-muted-foreground mb-2">
                                <History className="h-3 w-3" />
                                Audit trail
                            </div>
                            <Card className="overflow-hidden">
                                {events.length === 0 ? (
                                    <div className="p-4 text-xs text-muted-foreground text-center">
                                        No events recorded yet.
                                    </div>
                                ) : (
                                    <div className="divide-y divide-border">
                                        {events.map((e) => (
                                            <div key={e.id} className="p-3 text-sm" data-testid={`event-${e.id}`}>
                                                <div className="flex items-center justify-between flex-wrap gap-2">
                                                    <div className="font-medium capitalize">
                                                        {e.event_type.replace(/_/g, " ")}
                                                    </div>
                                                    <div className="text-[11px] text-muted-foreground font-mono">
                                                        {fmtDateTime(e.occurred_at)}
                                                    </div>
                                                </div>
                                                {e.reason ? (
                                                    <div className="text-xs text-muted-foreground mt-1">
                                                        {e.reason}
                                                    </div>
                                                ) : null}
                                                {e.admin_name ? (
                                                    <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">
                                                        by {e.admin_name}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </div>

                        {/* Action bar */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                            <Button
                                variant="outline"
                                onClick={() => onAction("clear")}
                                className="gap-1.5"
                                data-testid="action-clear"
                            >
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                Clear — no misconduct
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => onAction("warn")}
                                disabled={isRevoked}
                                className="gap-1.5 border-orange-300 text-orange-700 hover:bg-orange-50"
                                data-testid="action-warn"
                            >
                                <FileWarning className="h-4 w-4" />
                                Issue warning
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => onAction("revoke")}
                                disabled={!cert}
                                className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
                                data-testid="action-revoke"
                            >
                                <FileX2 className="h-4 w-4" />
                                Revoke &amp; reset
                            </Button>
                            {isRevoked ? (
                                <Button
                                    onClick={() => onAction("reenrol")}
                                    className="gap-1.5 bg-primary hover:bg-primary/90"
                                    data-testid="action-reenrol"
                                >
                                    <RotateCcw className="h-4 w-4" />
                                    Re-enrol (clean slate)
                                </Button>
                            ) : null}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function EvidenceCell({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-mono text-sm mt-0.5">{value}</div>
        </div>
    );
}

// ============================================================
// Action modal (clear / warn / revoke / reenrol)
// ============================================================
function ActionDialog({ actionType, dossier, onClose, onComplete }) {
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState("");
    const [custom, setCustom] = useState("");
    const [appealDays, setAppealDays] = useState(14);
    const [sendEmail, setSendEmail] = useState(true);
    const [confirmedPolicy, setConfirmedPolicy] = useState(false);
    // Re-enrol specific
    const [blockAi, setBlockAi] = useState(false);
    const [requireProctoring, setRequireProctoring] = useState(false);

    useEffect(() => {
        // Reset when dialog opens — and pre-fill Reason + Custom paragraph
        // from the dossier so the registrar edits a draft instead of typing
        // from scratch. Editable in the inputs below.
        const ev = dossier?.evidence || {};
        const summary = dossier?.summary || {};
        const student = summary.user?.name || "the student";
        const program = summary.program?.title || "your program";
        const certNum = dossier?.certificate?.certificate_number
            || dossier?.certificate?.id
            || "N/A";
        const flagged = ev.flagged_attempts_count ?? 0;
        const tabs = ev.tab_switches ?? 0;
        const blurs = ev.window_blurs ?? 0;
        const copy = ev.copy_attempts ?? 0;
        const focused = Math.round(ev.focused_minutes || 0);
        const expected = Math.round(ev.expected_minutes || 0);
        const pacePct = ev.pace_ratio_percent ?? null;
        const compDays = ev.completion_days ?? 0;
        const intendedDays = ev.intended_duration_days ?? 0;

        let draftReason = "";
        let draftCustom = "";
        if (actionType === "warn" || actionType === "revoke") {
            const parts = [];
            if (flagged) parts.push(`${flagged} flagged quiz attempt${flagged === 1 ? "" : "s"}`);
            if (tabs) parts.push(`${tabs} tab switches`);
            if (blurs) parts.push(`${blurs} window blurs`);
            if (copy) parts.push(`${copy} copy attempts`);
            const proctorPhrase = parts.length
                ? `Retrospective audit identified ${parts.join(", ")} during proctored assessments`
                : "Retrospective audit identified anomalous activity during proctored assessments";
            const pacePhrase = (pacePct != null && intendedDays)
                ? `, combined with completion at ${pacePct}% of intended duration (${compDays} days vs ${intendedDays}-day program)`
                : "";
            const focusedPhrase = (focused && expected)
                ? `. Focused study time recorded: ${focused} minutes against an expected ${expected} minutes`
                : "";
            draftReason = `${proctorPhrase}${pacePhrase}${focusedPhrase}. This pattern is inconsistent with honest completion under the Academic Integrity Policy.`;

            if (actionType === "revoke") {
                draftCustom = `Following review of the evidence packet, your ${program} credential (#${certNum}) has been revoked under the HCC Academic Integrity Policy and Dismissal Policy. Your previous progress on this program has been fully reset and your access window has been extended by 90 days from today. Sign in at hccbconline.com/login to restart from Chapter 1. The program time-gate will apply — completion will require a minimum of 60% focused study time per chapter.`;
            } else {
                draftCustom = `This warning is being issued in connection with ${student}'s enrolment in ${program}. The evidence detailed above has been recorded in the integrity register. You may continue the program, but any further incidents will be reviewed under the HCC Dismissal Policy. You may contest this warning within the appeal window under the Dispute Resolution Policy.`;
            }
        } else if (actionType === "reenrol") {
            draftCustom = `Clean-slate re-enrolment authorised following revocation of the prior credential. Enhanced integrity controls applied (block AI assistance and/or require proctoring as ticked). The new enrolment is bound by the diploma time-gate.`;
        } else if (actionType === "clear") {
            draftCustom = "";
        }

        setReason(draftReason);
        setCustom(draftCustom);
        setAppealDays(14);
        setSendEmail(true);
        setConfirmedPolicy(false);
        setBlockAi(actionType === "reenrol");
        setRequireProctoring(actionType === "reenrol");
    }, [actionType, dossier]);

    if (!actionType || !dossier?.summary) return null;
    const enrolmentId = dossier.summary.enrollment_id;

    const config = {
        clear: {
            title: "Clear case — no misconduct",
            description: "This releases the enrolment from review with no further action. The audit trail will record the decision.",
            confirmLabel: "Clear case",
            confirmClass: "bg-emerald-600 hover:bg-emerald-700",
            needsReason: false,
            needsPolicyAck: false,
        },
        warn: {
            title: "Issue formal academic integrity warning",
            description: "A registrar-signed warning will be added to the student record and emailed to the student under the Respectful & Fair Treatment of Students Policy. The warning is permanent.",
            confirmLabel: "Issue warning",
            confirmClass: "bg-orange-600 hover:bg-orange-700",
            needsReason: true,
            needsPolicyAck: true,
        },
        revoke: {
            title: "Revoke credential & reset progress",
            description: "Revokes the certificate, wipes all the student's progress, and emails them the cheating evidence with instructions to sign in and start over. Their previous credential remains permanently marked Revoked on the public verify page.",
            confirmLabel: "Revoke & reset",
            confirmClass: "bg-rose-600 hover:bg-rose-700",
            needsReason: true,
            needsPolicyAck: true,
        },
        reenrol: {
            title: "Re-enrol student — clean slate",
            description: "Creates a brand-new enrolment for the same program. The revoked enrolment stays on file as audit evidence. The new enrolment will be governed by the time-gate once it ships.",
            confirmLabel: "Re-enrol clean slate",
            confirmClass: "bg-primary hover:bg-primary/90",
            needsReason: false,
            needsPolicyAck: false,
        },
    }[actionType];

    const submit = async () => {
        if (config.needsReason && reason.trim().length < 4) {
            toast.error("Please provide a reason (4+ characters).");
            return;
        }
        if (config.needsPolicyAck && !confirmedPolicy) {
            toast.error("You must confirm the policy alignment.");
            return;
        }
        setBusy(true);
        try {
            if (actionType === "clear") {
                await api.post(`/admin/integrity/enrollments/${enrolmentId}/clear`, {
                    note: custom || null,
                });
                toast.success("Case cleared.");
            } else if (actionType === "warn") {
                const { data } = await api.post(`/admin/integrity/enrollments/${enrolmentId}/warn`, {
                    reason, custom_paragraph: custom || null,
                    appeal_days: appealDays, send_email: sendEmail,
                    confirmed_policy: true,
                });
                toast.success(
                    sendEmail && data.email?.sent
                        ? "Warning issued and emailed to student."
                        : "Warning issued.",
                );
            } else if (actionType === "revoke") {
                const { data } = await api.post(`/admin/integrity/enrollments/${enrolmentId}/revoke`, {
                    reason, custom_paragraph: custom || null,
                    appeal_days: appealDays, send_email: sendEmail,
                    confirmed_policy: true,
                });
                toast.success(
                    sendEmail && data.email?.sent
                        ? "Credential revoked and student notified."
                        : "Credential revoked.",
                );
            } else if (actionType === "reenrol") {
                await api.post(`/admin/integrity/enrollments/${enrolmentId}/reenrol-clean-slate`, {
                    note: custom || null,
                    block_ai_assistance: blockAi,
                    require_proctoring: requireProctoring,
                });
                toast.success("Student re-enrolled with a clean slate.");
            }
            await onComplete();
        } catch (e) {
            toast.error(fmtError(e, "Action failed."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!actionType} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid={`action-dialog-${actionType}`}>
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">{config.title}</DialogTitle>
                    <DialogDescription>{config.description}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-1">
                    {config.needsReason ? (
                        <div>
                            <Label className="text-xs font-semibold">Reason of record</Label>
                            <Input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="e.g. Failed pace audit + 5 flagged attempts"
                                data-testid="action-reason-input"
                            />
                        </div>
                    ) : null}
                    <div>
                        <Label className="text-xs font-semibold">
                            {actionType === "reenrol" ? "Internal note (optional)"
                                : actionType === "clear" ? "Note for the record (optional)"
                                    : "Custom paragraph for the email (optional)"}
                        </Label>
                        <Textarea
                            value={custom}
                            onChange={(e) => setCustom(e.target.value)}
                            placeholder="Free-form note that will be appended verbatim…"
                            rows={3}
                            data-testid="action-custom-input"
                        />
                    </div>

                    {(actionType === "warn" || actionType === "revoke") ? (
                        <>
                            <div className={actionType === "warn" ? "grid grid-cols-2 gap-3" : ""}>
                                {actionType === "warn" ? (
                                    <div>
                                        <Label className="text-xs font-semibold">Appeal window (days)</Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={60}
                                            value={appealDays}
                                            onChange={(e) => setAppealDays(parseInt(e.target.value, 10) || 14)}
                                            data-testid="action-appeal-days"
                                        />
                                    </div>
                                ) : null}
                                <div className="flex items-end">
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <Checkbox
                                            checked={sendEmail}
                                            onCheckedChange={(v) => setSendEmail(!!v)}
                                            data-testid="action-send-email"
                                        />
                                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                                        Email the student
                                    </label>
                                </div>
                            </div>
                            {actionType === "revoke" ? (
                                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900" data-testid="revoke-warning-block">
                                    <strong>This will:</strong> revoke the certificate (public verify shows
                                    "Revoked — Academic Misconduct"), wipe all the student's progress and
                                    quiz attempts for this program, reset their enrolment to Chapter 1
                                    with a fresh 90-day access window, and email them the cheating-evidence
                                    log plus instructions to log in and start over.
                                </div>
                            ) : null}
                            <label className="flex items-start gap-2 cursor-pointer text-sm border border-primary/20 bg-primary/5 p-3 rounded-md">
                                <Checkbox
                                    checked={confirmedPolicy}
                                    onCheckedChange={(v) => setConfirmedPolicy(!!v)}
                                    className="mt-0.5"
                                    data-testid="action-confirm-policy"
                                />
                                <span className="text-xs leading-relaxed">
                                    I confirm this action aligns with the
                                    <strong> Respectful &amp; Fair Treatment of Students Policy</strong> and the
                                    <strong> HCC Dismissal Policy</strong>, and the evidence supporting it
                                    is documented in the integrity events register.
                                </span>
                            </label>
                        </>
                    ) : null}

                    {actionType === "reenrol" ? (
                        <div className="space-y-2 border border-primary/20 bg-primary/5 p-3 rounded-md">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <Checkbox
                                    checked={blockAi}
                                    onCheckedChange={(v) => setBlockAi(!!v)}
                                    data-testid="reenrol-block-ai"
                                />
                                Disable AI assistance features for this re-enrolment
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <Checkbox
                                    checked={requireProctoring}
                                    onCheckedChange={(v) => setRequireProctoring(!!v)}
                                    data-testid="reenrol-require-proctoring"
                                />
                                Require proctoring on chapter assessments
                            </label>
                        </div>
                    ) : null}
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy}
                        className={`gap-1.5 text-white ${config.confirmClass}`}
                        data-testid="action-confirm-btn"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScrollText className="h-4 w-4" />}
                        {config.confirmLabel}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


// ============================================================
// Time-gate announcement dialog (manual send to in-progress diploma students)
// ============================================================
function AnnouncementDialog({ open, onOpenChange }) {
    const [loading, setLoading] = useState(true);
    const [eligible, setEligible] = useState([]);
    const [transitionDays, setTransitionDays] = useState(14);
    const [selected, setSelected] = useState(new Set());
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/admin/integrity/announcement-list")
            .then(({ data }) => {
                setEligible(data.items || []);
                setSelected(new Set((data.items || []).map((i) => i.user.id)));
            })
            .catch(() => toast.error("Could not load eligible students."))
            .finally(() => setLoading(false));
    }, [open]);

    const toggle = (uid) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(uid)) next.delete(uid); else next.add(uid);
            return next;
        });
    };

    const send = async () => {
        if (selected.size === 0) {
            toast.error("Pick at least one student to send to.");
            return;
        }
        if (!window.confirm(
            `Send the strict-legal time-gate notice to ${selected.size} student(s)? This is permanent: the email will be recorded on each student's file.`,
        )) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/integrity/announce-time-gate", null, {
                params: {
                    transition_days: transitionDays,
                    user_ids: Array.from(selected).join(","),
                },
            });
            toast.success(
                `Sent ${data.sent}, skipped ${data.skipped} (already announced)${data.failed ? `, failed ${data.failed}` : ""}.`,
            );
            onOpenChange(false);
        } catch (e) {
            toast.error(fmtError(e, "Send failed."));
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[88vh] overflow-y-auto"
                data-testid="announce-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-xl flex items-center gap-2">
                        <Mail className="h-5 w-5 text-primary" />
                        Send time-gate announcement
                    </DialogTitle>
                    <DialogDescription>
                        Notifies in-progress diploma students that the 60% focused-time gate
                        begins enforcing on the date below. The email is strict in tone and
                        cites the Respectful &amp; Fair Treatment of Students Policy. Each
                        send is recorded permanently and is idempotent — students already
                        announced will be skipped.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 pt-1">
                    <div>
                        <Label className="text-xs font-semibold">Transition window (days)</Label>
                        <Input
                            type="number"
                            min={1}
                            max={60}
                            value={transitionDays}
                            onChange={(e) => setTransitionDays(parseInt(e.target.value, 10) || 14)}
                            data-testid="announce-days"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                            The email tells students the gate begins on
                            today + {transitionDays} days.
                        </p>
                    </div>

                    {loading ? (
                        <Skeleton className="h-32 w-full" />
                    ) : eligible.length === 0 ? (
                        <div className="text-xs text-muted-foreground text-center p-6 border border-dashed rounded">
                            No eligible students — every active in-progress diploma student has
                            already been notified.
                        </div>
                    ) : (
                        <div className="border rounded-md overflow-hidden max-h-72 overflow-y-auto">
                            <div className="flex justify-between px-3 py-2 bg-muted/50 text-[11px] uppercase tracking-wider font-semibold">
                                <span>{selected.size} of {eligible.length} selected</span>
                                <button
                                    type="button"
                                    onClick={() => setSelected(s =>
                                        s.size === eligible.length
                                            ? new Set()
                                            : new Set(eligible.map(e => e.user.id)),
                                    )}
                                    className="text-primary hover:underline"
                                    data-testid="announce-toggle-all"
                                >
                                    {selected.size === eligible.length ? "Clear all" : "Select all"}
                                </button>
                            </div>
                            <div className="divide-y divide-border" data-testid="announce-list">
                                {eligible.map((item) => {
                                    const on = selected.has(item.user.id);
                                    return (
                                        <label
                                            key={item.enrollment_id}
                                            className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-muted/30"
                                            data-testid={`announce-row-${item.user.id}`}
                                        >
                                            <Checkbox
                                                checked={on}
                                                onCheckedChange={() => toggle(item.user.id)}
                                                className="mt-0.5"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium">
                                                    {item.user.name || item.user.email}
                                                </div>
                                                <div className="text-xs text-muted-foreground truncate">
                                                    {item.user.email} · {item.program.title}
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-border">
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={send}
                        disabled={sending || selected.size === 0 || loading}
                        className="gap-1.5 bg-primary hover:bg-primary/90"
                        data-testid="announce-send-btn"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        Send to {selected.size}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
